/**
 * A plan keeps the pattern versions it was authorized under (migration 938).
 * When a type's pattern has moved on, a person who may amend the plan adopts
 * the new version — for one item or every item of that type in the plan — or
 * keeps the earlier one on purpose. Either needs a reason and goes in the
 * plan's history. Everyone else is told who decides.
 *
 *   PlanPatternDecision  the two buttons and the short form (item page drift
 *                        line; plan page row)
 *   PlanPatternChanges   the plan page's list of types waiting for a decision
 */

import { useCallback, useEffect, useState } from 'react';
import { LABEL_MONO } from '@/components/mono/primitives';
import { decidePlanPattern, fetchPlanPatternChanges, type PlanPatternChange } from '@/lib/itemPlanDriftService';
import { useTypeListPickers } from '@/hooks/prioritization/useTypeListPickers';

const BTN = 'border border-[var(--rule)] px-2.5 py-1 text-xs text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50';
const LIST_NAME: Record<string, string> = { work_type: 'work type', launch_type: 'launch type', investment_type: 'investment type' };

export function PlanPatternDecision({
  planCycleId,
  listKey,
  typeValue,
  typeLabel,
  wasVersion,
  nowVersion,
  itemId,
  itemsOfType,
  mayDecide,
  onDecided,
}: {
  planCycleId: string;
  listKey: string;
  typeValue: string;
  typeLabel: string;
  wasVersion: number | null;
  nowVersion: number;
  /** Set on the item page: the decision can be for this item only. */
  itemId: string | null;
  /** On the plan page: how many of the plan's items have this type. */
  itemsOfType?: number;
  mayDecide: boolean;
  onDecided: () => void;
}) {
  const [choice, setChoice] = useState<'adopt' | 'keep' | null>(null);
  const [scope, setScope] = useState<'item' | 'all'>(itemId ? 'item' : 'all');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!mayDecide) {
    return (
      <p className="text-xs text-[var(--text-ter)]" data-testid="plan-pattern-ask">
        The plan&rsquo;s owner, its executive sponsor or a workspace admin decides whether the plan adopts v{nowVersion} or keeps v{wasVersion ?? '—'}.
      </p>
    );
  }

  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    setError(null);
    const r = await decidePlanPattern({
      planCycleId,
      listKey,
      typeValue,
      decision: choice,
      itemId: scope === 'item' ? itemId : null,
      reason: reason.trim(),
    });
    setBusy(false);
    if (r.error) return setError(r.error);
    setChoice(null);
    setReason('');
    onDecided();
  };

  return (
    <div className="space-y-2" data-testid="plan-pattern-decision">
      {choice === null ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={BTN} onClick={() => setChoice('adopt')}>
            Adopt v{nowVersion}
          </button>
          <button type="button" className={BTN} onClick={() => setChoice('keep')}>
            Keep v{wasVersion ?? '—'}
          </button>
        </div>
      ) : (
        <div className="space-y-2 border border-[var(--rule)] p-3 text-sm text-[var(--text-sec)]">
          <p>
            {choice === 'adopt'
              ? `The plan will run on v${nowVersion} of the ${typeLabel} pattern`
              : `The plan will stay on v${wasVersion ?? '—'} of the ${typeLabel} pattern, and stop flagging v${nowVersion}`}
            {itemId ? '' : ` for ${itemsOfType ?? 'every'} item${itemsOfType === 1 ? '' : 's'} of that ${LIST_NAME[listKey] ?? 'type'}`}.
          </p>
          {itemId && (
            <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="For which items">
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={scope === 'item'} onChange={() => setScope('item')} /> This item
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} /> Every {typeLabel} item in the plan
              </label>
            </div>
          )}
          <label className="block">
            <span className="text-xs text-[var(--text-ter)]">Why (goes in the plan&rsquo;s history)</span>
            <input
              className="mt-1 w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={choice === 'adopt' ? 'e.g. the longer runway matches what we learned' : 'e.g. mid-launch; keep the plan we committed to'}
            />
          </label>
          {error && <p className="text-xs text-[var(--coral)]">{error}</p>}
          <div className="flex gap-2">
            <button type="button" className={BTN} disabled={busy || !reason.trim()} onClick={() => void submit()}>
              {busy ? 'Saving…' : choice === 'adopt' ? `Adopt v${nowVersion}` : `Keep v${wasVersion ?? '—'}`}
            </button>
            <button type="button" className="text-xs underline text-[var(--text-ter)]" onClick={() => setChoice(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function PlanPatternChanges({ planCycleId }: { planCycleId: string }) {
  const { typeLabel } = useTypeListPickers();
  const [state, setState] = useState<{ mayDecide: boolean; changes: PlanPatternChange[] } | null>(null);
  const load = useCallback(() => {
    fetchPlanPatternChanges(planCycleId)
      .then(setState)
      .catch(() => setState(null));
  }, [planCycleId]);
  useEffect(load, [load]);
  if (!state || state.changes.length === 0) return null;

  return (
    <div className="mt-3 border border-[var(--rule)] bg-[var(--surface)] px-4 py-3" data-testid="plan-pattern-changes">
      <div className={LABEL_MONO}>Patterns changed since this plan was authorized</div>
      <p className="mt-1 text-xs text-[var(--text-ter)]">The plan keeps the versions it was authorized under until someone adopts the new one.</p>
      <div className="mt-2 divide-y divide-[var(--rule)]">
        {state.changes.map((c) => {
          const label = typeLabel(c.listKey, c.typeValue);
          return (
            <div key={`${c.listKey}:${c.typeValue}`} className="space-y-1.5 py-2.5">
              <p className="text-sm text-[var(--text-sec)]">
                <strong className="text-[var(--text-pri)]">{label}</strong> ({LIST_NAME[c.listKey]}): v{c.wasVersion ?? '—'} → v{c.nowVersion}
                {c.reason ? ` — “${c.reason}”` : ''} · {c.items} item{c.items === 1 ? '' : 's'} in this plan
              </p>
              <PlanPatternDecision
                planCycleId={planCycleId}
                listKey={c.listKey}
                typeValue={c.typeValue}
                typeLabel={label}
                wasVersion={c.wasVersion}
                nowVersion={c.nowVersion}
                itemId={null}
                itemsOfType={c.items}
                mayDecide={state.mayDecide}
                onDecided={load}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
